import { useState, type ReactNode } from 'react';
import {
  Button,
  EmptyState,
  Modal,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  Tooltip,
  TR,
} from '@/components/ui';
import { MONEY_NUM_FMT } from '@/components/dashboards/drilldown/drilldown-columns-shared';
import { Money } from '@/components/ui/Money';
import { sumDecimalStrings } from '@/lib/decimal';
import { exportRowsToXlsx, type XlsxExportColumn } from '@/lib/xlsx-export';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { formatPercent } from '@/lib/commissions';
import { formatDateOnly } from '@/lib/format';
import { excludedByCurrencyNote } from '@/lib/targets';
import { ApiError } from '@/lib/api';
import type { CommissionNewBusinessParams } from '@/lib/types';
import { useCommissionNewBusiness } from '@/lib/use-commission-new-business';

/**
 * New business per account (owner 2026-10-28): the per-account breakdown behind
 * "Accounts this period", "Same accounts last period", "New business" and
 * "New business commission", opened from Management -> Financial Analytics
 * "Targets & commission" and the University Partnerships commission strip.
 *
 * - Every amount is in the measured currency (`meta.measuredInCurrency`), so
 *   one `currency` serves the whole table. Money stays a decimal string.
 * - `null` renders `—` (Money / formatPercent do that); a companyless row
 *   (`companyId: null`, e.g. a deleted company on a frozen statement) is plain
 *   text, never a link.
 * - `showCommission` adds the New business commission column. Remainder users
 *   have no commission: the caller passes `false`, or the modal derives it from
 *   `meta.newBusinessCommission === null`.
 */

export interface NewBusinessAccountsTableRow {
  companyId: number | null;
  companyName: string;
  thisPeriod: string;
  lastPeriod: string;
  growth: string;
  sharePercent: string | null;
  newBusiness: string;
  newBusinessCommission?: string | null;
}

export interface NewBusinessAccountsTableProps {
  rows: readonly NewBusinessAccountsTableRow[];
  currency: string | null;
  showCommission: boolean;
}

/**
 * One column list serves the table, the phone cards and the Excel export, so
 * the three cannot drift. `value` is the raw decimal string (or name).
 */
type ColumnKind = 'account' | 'money' | 'percent';

/** Share % arrives as a 4dp percentage figure (e.g. "62.5000"), not a fraction. */
const PERCENT_NUM_FMT = '0.00##';

interface Column {
  key: string;
  label: string;
  kind: ColumnKind;
  value: (row: NewBusinessAccountsTableRow) => string | null;
}

function columnsFor(showCommission: boolean): Column[] {
  const columns: Column[] = [
    { key: 'account', label: 'Account', kind: 'account', value: (row) => row.companyName },
    { key: 'thisPeriod', label: 'This period', kind: 'money', value: (row) => row.thisPeriod },
    { key: 'lastPeriod', label: 'Last period', kind: 'money', value: (row) => row.lastPeriod },
    { key: 'growth', label: 'Growth', kind: 'money', value: (row) => row.growth },
    { key: 'sharePercent', label: 'Share %', kind: 'percent', value: (row) => row.sharePercent },
    { key: 'newBusiness', label: 'New business', kind: 'money', value: (row) => row.newBusiness },
  ];
  if (showCommission) {
    columns.push({
      key: 'newBusinessCommission',
      label: 'New business commission',
      kind: 'money',
      value: (row) => row.newBusinessCommission ?? null,
    });
  }
  return columns;
}

function renderCell(column: Column, row: NewBusinessAccountsTableRow, currency: string | null): ReactNode {
  switch (column.kind) {
    case 'account':
      if (row.companyId === null) return row.companyName;
      return <TextLink to={`/companies/${row.companyId}`}>{row.companyName}</TextLink>;
    case 'percent':
      return <span className="tabular-nums">{formatPercent(column.value(row))}</span>;
    case 'money':
      return <Money amount={column.value(row)} currency={currency} />;
  }
}

function rowKey(row: NewBusinessAccountsTableRow, index: number): string {
  return row.companyId !== null ? `c${row.companyId}` : `n${index}`;
}

export function NewBusinessAccountsTable({ rows, currency, showCommission }: NewBusinessAccountsTableProps) {
  if (rows.length === 0) {
    return <EmptyState title="No accounts are behind this figure." />;
  }
  const columns = columnsFor(showCommission);

  return (
    <>
      <div className="hidden md:block">
        <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
          <Table>
            <THead>
              <TR>
                {columns.map((column) => (
                  <TH key={column.key} numeric={column.kind !== 'account'}>
                    {column.label}
                  </TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {rows.map((row, index) => (
                <TR key={rowKey(row, index)}>
                  {columns.map((column) => (
                    <TD key={column.key} numeric={column.kind !== 'account'}>
                      {renderCell(column, row, currency)}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <ul className="divide-y divide-border rounded-md border border-border md:hidden">
        {rows.map((row, index) => (
          <li key={rowKey(row, index)} className="px-3 py-3">
            <dl className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1.5 text-sm">
              {columns.map((column) => (
                <div key={column.key} className="contents">
                  <dt className="truncate text-xs text-fg-muted">{column.label}</dt>
                  <dd className="min-w-0 break-words">{renderCell(column, row, currency)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

export interface NewBusinessBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  /** `null` fetches nothing. */
  params: CommissionNewBusinessParams | null;
  title: string;
}

function windowText(window: { from: string; to: string }): string {
  return `${formatDateOnly(window.from)} – ${formatDateOnly(window.to)}`;
}

function errorText(error: Error): string {
  if (error instanceof ApiError) return error.formMessage ?? error.message;
  return error.message;
}

export function NewBusinessBreakdownModal({ open, onClose, params, title }: NewBusinessBreakdownModalProps) {
  const state = useCommissionNewBusiness(open ? params : null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const response = state.status === 'ready' ? state.data : null;
  const meta = response?.meta ?? null;
  const rows = response?.data ?? [];

  const excludedCount =
    meta !== null
      ? meta.accountsThisPeriod.excludedDocuments + meta.accountsLastPeriod.excludedDocuments
      : 0;
  const excludedNote = excludedCount > 0 ? excludedByCurrencyNote(excludedCount) : null;

  const showCommission = meta !== null && meta.newBusinessCommission !== null;
  const subtitle =
    meta !== null
      ? `${windowText(meta.window)} vs ${windowText(meta.previousWindow)} · ${meta.measuredInCurrency}`
      : null;
  const description = subtitle !== null ? <span>{subtitle}</span> : undefined;

  async function handleExport() {
    if (meta === null || subtitle === null || rows.length === 0) return;
    const columns = columnsFor(showCommission);
    const exportColumns: XlsxExportColumn<NewBusinessAccountsTableRow>[] = columns.map((column) => {
      switch (column.kind) {
        case 'account':
          return { key: column.key, label: column.label, value: column.value };
        case 'percent':
          return { key: column.key, label: column.label, value: column.value, numFmt: PERCENT_NUM_FMT };
        case 'money':
          return {
            key: column.key,
            label: `${column.label} (${meta.measuredInCurrency})`,
            value: column.value,
            numFmt: MONEY_NUM_FMT,
          };
      }
    });
    const totals: Record<string, string> = {};
    for (const column of columns) {
      if (column.kind !== 'money') continue;
      const total = sumDecimalStrings(rows.map((row) => column.value(row)));
      if (total !== null) totals[column.key] = total;
    }
    setExporting(true);
    setExportError(null);
    try {
      await exportRowsToXlsx({
        fileName: `seed-emerge-new-business-${meta.window.from}-${meta.window.to}.xlsx`,
        sheetName: 'New business',
        title: [title, subtitle, ...(excludedNote !== null ? [excludedNote.short] : [])],
        columns: exportColumns,
        rows,
        totals,
      });
    } catch {
      setExportError('The Excel file could not be created.');
    } finally {
      setExporting(false);
    }
  }

  const exportDisabledReason =
    state.status === 'loading' || state.status === 'idle'
      ? 'Loading'
      : meta === null || rows.length === 0
        ? 'No accounts to export'
        : null;
  const exportButton = (
    <Button
      variant="secondary"
      size="sm"
      loading={exporting}
      disabled={exportDisabledReason !== null}
      onClick={() => void handleExport()}
    >
      Export to Excel
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      {...(description !== undefined ? { description } : {})}
      headerActions={
        exportDisabledReason !== null ? (
          <Tooltip label={exportDisabledReason}>{exportButton}</Tooltip>
        ) : (
          exportButton
        )
      }
    >
      <div className="flex flex-col gap-3">
        {exportError !== null && (
          <Notice tone="danger" role="alert">
            {exportError}
          </Notice>
        )}

        {(state.status === 'loading' || state.status === 'idle') && (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {state.status === 'forbidden' && (
          <Notice tone="danger" role="alert">
            Your permission set does not include this. An administrator can change that.
          </Notice>
        )}

        {state.status === 'not-found' && (
          <Notice tone="danger" role="alert">
            There is no commission assignment or remainder target for this person in this period.
          </Notice>
        )}

        {state.status === 'error' && state.error !== null && (
          <Notice
            tone="danger"
            role="alert"
            action={
              <Button variant="secondary" size="sm" onClick={state.reload}>
                Retry
              </Button>
            }
          >
            {errorText(state.error)}
          </Notice>
        )}

        {meta !== null && (
          <>
            {excludedNote !== null && (
              <p className="text-xs text-fg-muted" title={excludedNote.detail}>
                {excludedNote.short}
              </p>
            )}
            <NewBusinessAccountsTable
              rows={rows}
              currency={meta.measuredInCurrency}
              showCommission={showCommission}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
